import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useParams,useNavigate  } from "react-router-dom";
import { db, auth } from "../../firebase-config";
import { collection, query, where, getDocs, setDoc, doc, getDoc } from "firebase/firestore";

const ProductView = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [userPrice, setUserPrice] = useState(null);
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const currentUser = auth.currentUser;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        if (!productId) {
          setError("Invalid product ID.");
          setLoading(false);
          return;
        }

        const productRef = doc(db, "products", productId);
        const productSnap = await getDoc(productRef);

        if (productSnap.exists()) {
          const productData = productSnap.data();
          setProduct({ id: productSnap.id, ...productData });

          // Fetch options if available
          const optionsRef = collection(db, "products", productId, "options");
          const optionsSnap = await getDocs(optionsRef);
          setOptions(optionsSnap.docs.map((doc) => doc.data()));
        } else {
          setError("Product not found.");
        }
      } catch (err) {
        console.error("Error fetching product:", err);
        setError("Failed to load product.");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  useEffect(() => {
    const fetchUserPrice = async () => {
      if (!currentUser || !productId) return;

      try {
        let userPriceSnap = null;
        let companyPriceSnap = null;

        // Check if user has a specific price
        const userPriceRef = doc(db, "products", productId, "pricing", currentUser.uid);
        userPriceSnap = await getDoc(userPriceRef);

        // Check if company has a price (if user is linked to a company)
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        const companyId = userSnap.exists() ? userSnap.data().companyId : null;

        if (companyId) {
          const companyPriceRef = doc(db, "products", productId, "pricing", companyId);
          companyPriceSnap = await getDoc(companyPriceRef);
        }

        // Set user price based on available data
        if (userPriceSnap.exists()) {
          setUserPrice(userPriceSnap.data().price);
        } else if (companyPriceSnap && companyPriceSnap.exists()) {
          setUserPrice(companyPriceSnap.data().price);
        } else {
          setUserPrice(product?.defaultPrice || 0);
        }
      } catch (err) {
        console.error("Error fetching user-specific price:", err);
      }
    };

    fetchUserPrice();
  }, [currentUser, productId, product]);

  const calculateFinalPrice = () => {
    let finalPrice = userPrice ?? product?.defaultPrice ?? 0;
    if (selectedOption) {
      const optionData = options.find((opt) => opt.name === selectedOption);
      if (optionData && optionData.additionalPrice) {
        finalPrice += optionData.additionalPrice;
      }
    }
    return finalPrice;
  };

  const handleAddToCart = async () => {
    if (!currentUser) {
      setError("You must be logged in to add items to the cart.");
      return;
    }

    if (!product) {
      setError("Product data is not available.");
      return;
    }

    try {
      const cartItemRef = doc(db, "carts", currentUser.uid, "items", product.id);
      const cartSnap = await getDoc(cartItemRef);

      if (cartSnap.exists()) {
        // If item exists, update quantity
        const existingItem = cartSnap.data();
        await setDoc(cartItemRef, {
          ...existingItem,
          quantity: existingItem.quantity + quantity,
        });
      } else {
        // If item does not exist, add it
        await setDoc(cartItemRef, {
          productId: product.id,
          name: product.name || "Unnamed Product",
          price: calculateFinalPrice(),
          imageUrl: product.imageUrl || "https://via.placeholder.com/300",
          quantity: quantity,
          option: selectedOption || null,
          addedAt: new Date(),
        });
      }

      alert("Product added to cart!");
      navigate("/cart")
    } catch (err) {
      console.error("Error adding to cart:", err);
      setError("Failed to add product to cart.");
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container>
      <Card sx={{ maxWidth: 600, margin: "auto", mt: 5 }}>
        <CardMedia
          component="img"
          height="300"
          image={product.imageUrl || "https://via.placeholder.com/300"}
          alt={product.name}
        />
        <CardContent>
          <Typography variant="h5">{product.name}</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            {product.description || "No description available."}
          </Typography>
          <Typography variant="h6" sx={{ mt: 2 }}>
            Price: ${calculateFinalPrice().toFixed(2)}
          </Typography>

          {/* Options Selection */}
          {options.length > 0 && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Options</InputLabel>
              <Select
                value={selectedOption}
                onChange={(e) => setSelectedOption(e.target.value)}
              >
                {options.map((option, index) => (
                  <MenuItem key={index} value={option.name}>
                    {option.name} {option.additionalPrice ? `(+ $${option.additionalPrice})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Quantity Selection & Add to Cart */}
          <Box sx={{ mt: 3, display: "flex", alignItems: "center" }}>
            <TextField
              type="number"
              label="Quantity"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              sx={{ width: 100, mr: 2 }}
            />
            <Button variant="contained" color="primary" onClick={handleAddToCart}>
              Add to Cart
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ProductView;
